import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { FC, ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { createPortal } from "react-dom";
import { api as defaultApi, VerityApi } from "../api/fetch";
import { CoreToastContainer, StepConfig } from "../core";
import type { CoreFileItem } from "../core";
import ThemeWrapper from "../theme";
import { EditQuizModalSkeleton } from "./components";
import { EditQuizToastContext } from "./context/EditQuizContext";
import { EditQuizPage, EditQuizPageProps, EditQuizRef, ExemptionSectionInfo } from "./editQuiz";
import { UnsavedAssessmentInfo } from "./editQuizModalWrapper";
import useEditQuizWrapper, { UseEditQuizWrapperProps, UseEditQuizWrapperReturn } from "./hooks/useEditQuizWrapper";
import { EditQuizAssessmentUUids, PendingLiveSession } from "./live/liveHelper";
import { EditQuizStep } from "./proctoring-options/option-text/ProctoringOptionText";
import type { ThirdPartyPlatform } from "../types/common";

export type EditQuizWrapperProps = {
	quizName?: string;
	api?: VerityApi;
	quizInfo: UnsavedAssessmentInfo;
	portalElement?: HTMLElement;
	verityOrigin?: string;
} & Pick<
	EditQuizPageProps,
	| "onSaveProfileChange"
	| "step"
	| "setStep"
	| "onBackEnabledChange"
	| "onNextEnabledChange"
	| "onSaveEnabledChange"
	| "setIsFinalPage"
	| "setIsFirstPage"
	| "onSaveOptions"
	| "uploadedExemptedStudentsMap"
	| "setUploadedExemptedStudentsMap"
	| "uploadedProfilesMap"
	| "setUploadedProfilesMap"
	| "logger"
	| "quizType"
	| "forceStartOnFirstPage"
	| "onStartSaveOptions"
	| "entryPoint"
	| "onClose"
	| "onSettingsLoadingChange"
	| "hideAccommodations"
	| "initialLiveSessions"
	| "initialReferenceMaterialFiles"
> &
	Pick<UseEditQuizWrapperProps, "onInitialOptions" | "onEnabledChange" | "initialAssessmentConfiguration">;

type EditQuizData = Pick<
	UseEditQuizWrapperReturn["state"],
	"proctoringAssessmentConfiguration" | "proctoringOption"
> & {
	activeProfile: ProctoringOptionProfileV2 | undefined;
	pendingLiveSessions: PendingLiveSession[];
	exemptionInfo?: ExemptionSectionInfo;
	referenceMaterialFiles?: CoreFileItem[];
	thirdPartyPlatforms: ThirdPartyPlatform[];
};

export type EditQuizWrapperRef = {
	closeButtonHandler: () => void;
	saveAsProfile: () => void;
	setEnabled: (enabled: boolean) => void;
	nextButtonHandler: () => void;
	backButtonHandler: () => void;
	goToPage: (pageName: EditQuizStep) => void;
	getStepKeyByIndex: (index: number) => EditQuizStep;
	getStepConfig: () => StepConfig<EditQuizStep>[];
	/**For confirmation step in the verity assessment workflow */
	getQuizData: () => EditQuizData;
} & Pick<
	EditQuizRef,
	| "saveLiveSessionHandler"
	| "saveButtonHandler"
	| "saveReferenceMaterials"
	| "validateReferenceMaterialSetting"
	| "markCurrentStepCompleted"
>;

export type EditQuizSwitchComponent = FC<{
	enabled: boolean;
	setEnabled: (_: boolean) => void;
}>;

export const EditQuizWrapper = forwardRef(function EditQuizWrapper(
	{
		api = defaultApi,
		quizInfo,
		portalElement,
		onStartSaveOptions,
		onSaveOptions,
		onInitialOptions,
		onEnabledChange,
		initialAssessmentConfiguration,
		onBackEnabledChange,
		onNextEnabledChange,
		onSaveEnabledChange,
		setIsFinalPage,
		uploadedExemptedStudentsMap,
		setUploadedExemptedStudentsMap,
		uploadedProfilesMap,
		setUploadedProfilesMap,
		forceStartOnFirstPage,
		logger,
		step,
		setStep,
		onSaveProfileChange,
		quizName,
		quizType,
		setIsFirstPage,
		entryPoint,
		verityOrigin,
		onClose,
		onSettingsLoadingChange,
		hideAccommodations,
		initialLiveSessions,
		initialReferenceMaterialFiles,
	}: EditQuizWrapperProps,
	ref: ForwardedRef<EditQuizWrapperRef>,
) {
	const currFakeLiveSessionId = useRef(0);
	const assessmentUuids = useRef<EditQuizAssessmentUUids | undefined>();

	const editQuizPageRef = useRef<EditQuizRef>(null);

	const { state, action } = useEditQuizWrapper({
		quizType,
		api,
		quizInfo,
		onInitialOptions,
		logger,
		onEnabledChange,
		initialAssessmentConfiguration,
	});

	const {
		currentProfileId,
		defaultProctoringOption,
		enforcedProfilesOnly,
		initialProctoringAssessmentConfiguration,
		proctoringAssessmentConfiguration,
		initialProctoringOption,
		proctoringOption,
		pendingLiveSessions,
		pendingLiveSessionIds,
		existingLiveSessions,
		pendingLiveSessionsArray,
	} = state;

	const {
		refreshSettings,
		setProctoringAssessmentConfiguration,
		setProctoringOption,
		setPendingLiveSessions,
		setPendingLiveSessionIds,
		setCurrentProfileId,
		setExistingLiveSessions,
		handleEnabledChange,
	} = action;

	const saveButtonHandler: EditQuizWrapperRef["saveButtonHandler"] = async (password, additionalQuizInfo) =>
		(await editQuizPageRef.current?.saveButtonHandler(password, additionalQuizInfo)) ?? false;

	const closeButtonHandler: EditQuizWrapperRef["closeButtonHandler"] = () => {
		void editQuizPageRef.current?.closeButtonHandler();
	};

	const saveAsProfile: EditQuizWrapperRef["saveAsProfile"] = () => {
		editQuizPageRef.current?.saveAsProfileButtonHandler();
	};

	const nextButtonHandler: EditQuizWrapperRef["nextButtonHandler"] = () => {
		editQuizPageRef.current?.nextButtonHandler();
	};

	const backButtonHandler: EditQuizWrapperRef["backButtonHandler"] = () => {
		editQuizPageRef.current?.backButtonHandler();
	};

	const goToPage: EditQuizWrapperRef["goToPage"] = (pageName: EditQuizStep) => {
		editQuizPageRef.current?.goToPage(pageName);
	};

	const getStepKeyByIndex: EditQuizWrapperRef["getStepKeyByIndex"] = (index: number) => {
		return editQuizPageRef.current?.getStepKeyByIndex(index) ?? "confirmation";
	};

	const getStepConfig: EditQuizWrapperRef["getStepConfig"] = () => {
		return editQuizPageRef.current?.getStepConfig() ?? [];
	};

	const getQuizData: EditQuizWrapperRef["getQuizData"] = useCallback(() => {
		const activeProfile = editQuizPageRef.current?.getActiveProfile();

		const sanitizedProctoringOption =
			proctoringOption?.enableIpAddressRestriction && !proctoringOption.allowedIpAddresses?.length
				? { ...proctoringOption, enableIpAddressRestriction: false, allowedIpAddresses: undefined }
				: proctoringOption;

		if (sanitizedProctoringOption !== proctoringOption) {
			setProctoringOption(sanitizedProctoringOption);
		}

		return {
			proctoringOption: sanitizedProctoringOption,
			proctoringAssessmentConfiguration,
			pendingLiveSessions: pendingLiveSessionsArray,
			activeProfile,
			exemptionInfo: editQuizPageRef.current?.getExemptionSectionInfo(),
			referenceMaterialFiles: editQuizPageRef.current?.getReferenceMaterialFiles
				? editQuizPageRef.current.getReferenceMaterialFiles()
				: undefined,
			thirdPartyPlatforms: editQuizPageRef.current?.getThirdPartyPlatforms() ?? [],
		};
	}, [proctoringOption, proctoringAssessmentConfiguration, pendingLiveSessionsArray, setProctoringOption]);

	const saveReferenceMaterials = useCallback(async (quizId?: string, files?: CoreFileItem[]) => {
		await editQuizPageRef.current?.saveReferenceMaterials?.(quizId, files);
	}, []);

	const validateReferenceMaterialSetting: EditQuizWrapperRef["validateReferenceMaterialSetting"] = useCallback(
		(options?: { showToast?: boolean }) => {
			return editQuizPageRef.current?.validateReferenceMaterialSetting?.(options) ?? true;
		},
		[],
	);

	const markCurrentStepCompleted: EditQuizWrapperRef["markCurrentStepCompleted"] = useCallback(() => {
		editQuizPageRef.current?.markCurrentStepCompleted();
	}, []);

	const saveLiveSessionHandler: EditQuizWrapperRef["saveLiveSessionHandler"] = useCallback(async (quizId?: string) => {
		await editQuizPageRef.current?.saveLiveSessionHandler(quizId);
	}, []);

	useImperativeHandle(ref, () => ({
		saveButtonHandler,
		closeButtonHandler,
		setEnabled: handleEnabledChange,
		saveAsProfile,
		nextButtonHandler,
		backButtonHandler,
		goToPage,
		getStepConfig,
		getStepKeyByIndex,
		getQuizData,
		saveReferenceMaterials,
		validateReferenceMaterialSetting,
		markCurrentStepCompleted,
		saveLiveSessionHandler,
	}));

	useEffect(() => {
		void refreshSettings();
	}, []);

	let optionsPage;
	optionsPage = (
		<ThemeWrapper scoped={false}>
			<EditQuizPage
				quizName={quizName}
				ref={editQuizPageRef}
				api={api}
				quizInfo={quizInfo}
				initialProctoringAssessmentConfiguration={initialProctoringAssessmentConfiguration}
				proctoringAssessmentConfiguration={proctoringAssessmentConfiguration}
				setProctoringAssessmentConfiguration={setProctoringAssessmentConfiguration}
				initialProctoringOption={initialProctoringOption}
				proctoringOption={proctoringOption}
				setProctoringOption={setProctoringOption}
				existingLiveSessions={existingLiveSessions}
				setExistingLiveSessions={setExistingLiveSessions}
				pendingLiveSessions={pendingLiveSessions}
				setPendingLiveSessions={setPendingLiveSessions}
				pendingLiveSessionIds={pendingLiveSessionIds}
				setPendingLiveSessionIds={setPendingLiveSessionIds}
				currFakeLiveSessionId={currFakeLiveSessionId}
				initialLiveSessions={initialLiveSessions}
				initialReferenceMaterialFiles={initialReferenceMaterialFiles}
				assessmentUuids={assessmentUuids}
				currentProfileId={currentProfileId}
				setCurrentProfileId={setCurrentProfileId}
				enforcedProfilesOnly={enforcedProfilesOnly}
				onBackEnabledChange={onBackEnabledChange}
				onNextEnabledChange={onNextEnabledChange}
				onSaveEnabledChange={onSaveEnabledChange}
				setIsFinalPage={setIsFinalPage}
				forceStartOnFirstPage={forceStartOnFirstPage}
				onStartSaveOptions={(confirmedOptions) => {
					onStartSaveOptions?.();
					refreshSettings(confirmedOptions);
				}}
				onSaveOptions={onSaveOptions ?? (() => null)}
				uploadedExemptedStudentsMap={uploadedExemptedStudentsMap}
				setUploadedExemptedStudentsMap={setUploadedExemptedStudentsMap}
				uploadedProfilesMap={uploadedProfilesMap}
				setUploadedProfilesMap={setUploadedProfilesMap}
				logger={logger}
				defaultProctoringOption={defaultProctoringOption}
				step={step}
				setStep={setStep}
				quizType={quizType}
				entryPoint={entryPoint}
				onSaveProfileChange={onSaveProfileChange}
				setIsFirstPage={setIsFirstPage}
				onSettingsLoadingChange={onSettingsLoadingChange}
				verityOrigin={verityOrigin}
				onClose={onClose}
				hideAccommodations={hideAccommodations}
			/>
		</ThemeWrapper>
	);
	if (portalElement) {
		optionsPage = createPortal(optionsPage, portalElement);
	}

	const toastContainerId = `toast-${quizInfo.quizId}`;

	return (
		<EditQuizToastContext.Provider value={toastContainerId}>
			{!proctoringAssessmentConfiguration && <EditQuizModalSkeleton />}
			{optionsPage}
			<CoreToastContainer containerId={toastContainerId} enableMultiContainer />
		</EditQuizToastContext.Provider>
	);
});
